import { useRef, type ReactNode } from 'react';
import { useAuthenticatedMediaUrl } from '../hooks/useAuthenticatedMediaUrl';
import { useNearViewport } from '../hooks/useNearViewport';
import { downloadAuthenticatedFile } from '../utils/authenticatedStorage';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import { toast } from '../store/toastStore';

interface AuthenticatedImageProps {
  storageUrl: string;
  alt: string;
  className?: string;
  onClick?: () => void;
  /** FIX-B38 (QX-801): fetch the bytes only when the image nears the viewport (conversations). */
  lazy?: boolean;
}

export function AuthenticatedImage({ storageUrl, alt, className, onClick, lazy = false }: AuthenticatedImageProps) {
  const { t } = useTranslation();
  const holderRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(holderRef, lazy);
  const { url, loading, error } = useAuthenticatedMediaUrl(near ? storageUrl : undefined);

  if (loading || !near) {
    return (
      <div ref={holderRef} className={`bg-surface-100 animate-pulse ${className ?? 'w-12 h-12 rounded-xs'}`} aria-hidden />
    );
  }

  if (error || !url) {
    return (
      <div
        className={`flex items-center justify-center bg-surface-100 text-surface-500 ${className ?? 'w-12 h-12 rounded-xs'}`}
        title={t('tickets.fileLoadError')}
      >
        <ImageOff size={18} />
      </div>
    );
  }

  const img = <img src={url} alt={alt} className={className} loading="lazy" />;
  // A clickable image is a real button (keyboard and screen readers), not an <img onClick>.
  if (!onClick) return img;
  return (
    <button type="button" onClick={onClick} className="block">
      {img}
    </button>
  );
}

interface AuthenticatedDownloadLinkProps {
  storageUrl: string;
  filename: string;
  className?: string;
  children: ReactNode;
}

export function AuthenticatedDownloadLink({
  storageUrl,
  filename,
  className,
  children,
}: AuthenticatedDownloadLinkProps) {
  const { t } = useTranslation();
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    downloadAuthenticatedFile(storageUrl, filename).catch(() => {
      toast('error', t('common.downloadError'));
    });
  };

  // A button, not <a href="#">: the file needs an authenticated fetch, so there
  // is no URL the browser could open on its own.
  return (
    <button type="button" onClick={handleClick} className={className}>
      {children}
    </button>
  );
}
